import { ScaleContinuousNumeric } from 'd3-scale';
import { useMemo } from 'react';

import { useLocale } from '@tanium/react-locale-context';

import { NumberScale } from '../utils/NumberScale';
import { scaleLinear } from '../utils/scaleLinear';
import { scaleSymlog } from '../utils/scaleSymlog';
import { NumberScaleType } from '../utils/ScaleType';

export interface UseNumberScaleProps<TValueType> {
  /**
   * scale domain max.
   */
  domainMax: TValueType;
  /**
   * scale domain min.
   */
  domainMin: TValueType;
  /**
   * ending point of scale range.
   */
  rangeEnd: number;
  /**
   * starting point of scale range.
   */
  rangeStart: number;
  /**
   * scale type: linear or log.
   */
  scaleType: NumberScaleType;
  /**
   * Whether to round the start and end of the domain through d3's scale.nice(). Defaults to true.
   */
  domainRounding?: boolean;
}

/**
 * Hook that returns a number scale with locale aware tick formatting.
 */
export const useNumberScale = <TValueType extends number>({
  domainMax,
  domainMin,
  rangeEnd,
  rangeStart,
  scaleType,
  domainRounding = true,
}: UseNumberScaleProps<TValueType>): NumberScale => {
  const locale = useLocale();
  return useMemo(() => {
    const valueScale = (
      (scaleType === 'log' ? scaleSymlog : scaleLinear)(locale) as ScaleContinuousNumeric<number, number>
    )
      .domain([domainMin, domainMax])
      .range([rangeStart, rangeEnd]);
    if (domainRounding) {
      return valueScale.nice();
    }
    return valueScale;
  }, [scaleType, locale, domainMin, domainMax, rangeStart, rangeEnd, domainRounding]);
};
